<template>
    <api-delivery-card :icon="props.icon">
        <template v-slot:title>
            <slot name="title"></slot>
        </template>

        <template v-slot:headerRight>
            <vx-popover hover :arrow="true" placement="top">
                <vx-tabs v-model="mode" :disabled="!isPrettyAllowed" size="sm">
                    <vx-tab value="raw">
                        Raw
                    </vx-tab>
                    <vx-tab value="pretty">Pretty</vx-tab>
                </vx-tabs>
                <template v-if="!isPrettyAllowed" v-slot:content>
                    <span v-if="isBinary || !isJson">
                        Cannot format a non-JSON payload.
                    </span>
                    <span v-else-if="props.isTruncated">
                        Cannot format a truncated payload.
                    </span>
                </template>
            </vx-popover>
        </template>
        <template v-slot:content>
            <!-- match style to response content to reduce layout shift -->
            <div v-if="props.loading" class="w-full p-4 bg-slate-50 rounded-[10px] justify-start items-center gap-3 inline-flex h-[52px]">
                <vx-icon-loading spin></vx-icon-loading>
            </div>

            <template v-else-if="decodedBody !== null || isBinary">
                <div class="flex flex-col w-full gap-4">
                    <slot name="metadata"></slot>
                    <div v-if="payload || isBinary" class="flex flex-col w-full">
                        <div v-if="mode === 'raw'"
                            class="w-full px-5 py-3 bg-slate-50 rounded-2xl border border-solid border-slate-200 text-slate-500 text-sm font-normal leading-snug break-all">
                            <div v-if="isBinary"
                                class="w-full p-4 bg-slate-50 rounded-[10px] justify-start items-center gap-3 inline-flex">
                                <font-awesome-icon widthAuto :icon="faFileXmark" class="text-black text-base" />
                                <div class="text-slate-500 text-base">Binary file not shown.</div>
                            </div>
                            <template v-else>
                                {{ payload }}
                                <template v-if="props.isTruncated">
                                    <br />...
                                    <br />truncated
                                </template>
                            </template>
                        </div>
                        <div v-else class="w-full">
                            <vx-code-editor :modelValue="payload" disabled></vx-code-editor>
                        </div>
                    </div>
                </div>
            </template>

            <div v-else
                class="w-full p-4 bg-slate-50 rounded-[10px] justify-start items-center gap-3 inline-flex">
                <font-awesome-icon widthAuto :icon="faGhost" class="text-slate-500" />
                <div class="text-sky-950 text-sm font-normal">
                    <slot name="missing-message"></slot>
                </div>
            </div>
        </template>
    </api-delivery-card>
</template>

<script setup>
import ApiDeliveryCard from './ApiDeliveryCard.vue';
import { VxTabs, VxTab, VxCodeEditor, VxPopover, VxIconLoading } from '@voxie/frontend-components';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faGhost, faFileXmark } from '@fortawesome/pro-solid-svg-icons';
import { computed, ref, watch } from 'vue';
import { decodeBase64, isValidJsonString } from '~/utils/helpers';

const props = defineProps({
    code: {
        type: String,
        required: false,
    },
    contentType: {
        type: String,
        required: false,
    },
    isTruncated: {
        type: Boolean,
        required: false,
    },
    loading: {
        type: Boolean,
        required: false,
    },
    icon: {
        type: Object,
        required: false,
    },
});

const isJson = computed(() => {
    if (!props.contentType || !decodedBody.value) {
        return false;
    }

    return props.contentType.split(';')[0] === 'application/json' && isValidJsonString(decodedBody.value);
});

const decodedBody = computed(() => {
    if (typeof props.code !== 'string') {
        return null;
    }

    return decodeBase64(props.code);
})

const isBinary = computed(() => {
    if (!decodedBody.value) {
        return !!props.code;
    }

    return decodedBody.value.indexOf('�') >= 0;
})

const payload = computed(() => {
    if (!decodedBody.value || isBinary.value) {
        return null;
    }

    return props.isTruncated || !isJson.value ? decodedBody.value : JSON.stringify(JSON.parse(decodedBody.value), null, 4) // spacing level = 4
})

const mode = ref('raw');
const isPrettyAllowed = computed(() => !props.isTruncated && isJson.value && !isBinary.value);

watch(() => [props.code, props.contentType, props.isTruncated], () => {
    mode.value = !isPrettyAllowed.value ? 'raw' : 'pretty';
});
</script>
